import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Avatar,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  DragIndicator,
  Reorder,
  LinkedIn as LinkedInIcon,  Link as LinkIcon 
} from "@mui/icons-material";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";


export const resumeMeta = {
  hasPhoto: true,
  columns: 2,
  colorOptions: [
    { value: "#084c34", label: "Forest Green" },
    { value: "#1a2a6c", label: "Navy Blue" },
    { value: "#b22222", label: "Deep Red" },
    { value: "#2d3436", label: "Graphite" },
  ]
};

// Fixed pixel dimensions for each page size at 96dpi
const PAGE_SIZES = {
  A4:        { width: 794,  height: 1123 }, // 210mm x 297mm in pixels at 96dpi
  Letter:    { width: 816,  height: 1056 },
  Legal:     { width: 816,  height: 1344 },
  A3:        { width: 1123, height: 1587 },
  Executive: { width: 696,  height: 1010 },
};

const defaultData = {
  firstName: "John",
  lastName: "Doe",
  currentPosition: "Marketing Specialist",
  phone: "+123-456-7890",
  email: "hello@reallygreatsite.com",
  city: "123 Anywhere St., Any City",
  profile: "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat."
};

const defaultLeftSectionOrder = [
  'contact',
  'languages',
  'skills'
];

const defaultRightSectionOrder = [
  'profile',
  'education',
  'workExperience',
  'certifications',
  'accomplishments',
  'volunteering',
  'interests',
  'additionalInfo',
  'references',  'websites' 
];

const overflowText = {
  wordBreak: "break-word",
  overflowWrap: "break-word",
  whiteSpace: "pre-wrap",
  minWidth: 0,
};

const Resume39 = ({
  color = "#084c34",
  font = "Arial",
  fontSize = "14px",
  headingSize = "24px",
  fontStyle = "normal",
  sectionSpacing = 20,
  paragraphSpacing = 15,
  lineSpacing = 20,
  topBottomMargin = 20,
  sideMargins = 20,
  lineWeight = 1,
  pageSize = "A4",
  formData = {},
  photo = "",
  workExperiences = [],
  exporting = false,
  enableDragDrop = true,
  linkedIn = '',
}) => {
  const page = PAGE_SIZES[pageSize] || PAGE_SIZES.A4;
  
  const SIDEBAR_W = Math.round(page.width * 0.35);
  const MAIN_W    = page.width - SIDEBAR_W;
  
  const data = { 
    ...defaultData, 
    ...formData,
    linkedIn: linkedIn || formData.linkedIn || '',
  };
  
  const accentColor = "#f4c05c";
  
  const fs      = fontSize   || "14px";
  const hs      = headingSize || "24px";
  const lh      = `${lineSpacing}px`;
  const secGap  = sectionSpacing;
  const paraGap = paragraphSpacing;
  const tbPad   = topBottomMargin;
  const sidePad = sideMargins;

  // ── State ───────────────────────────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(0);
  const [pages, setPages]             = useState([]);   // [{ rightIds: string[] }]
  const mainContentRef                = useRef(null);   // hidden measure container

  const [educationEntries, setEducationEntries] = useState([]);
  const [savedSkills, setSavedSkills] = useState([]);
  const [savedSummary, setSavedSummary] = useState("");
  const [savedAdditionalInfo, setSavedAdditionalInfo] = useState([]);
  const [savedLanguages, setSavedLanguages] = useState([]);
  const [savedAccomplishments, setSavedAccomplishments] = useState([]);
  const [savedCertifications, setSavedCertifications] = useState([]);
  const [savedReferences, setSavedReferences] = useState([]);
  const [softwareSkills, setSoftwareSkills] = useState([]);
  const [savedVolunteering, setSavedVolunteering] = useState([]);
  const [savedInterests, setSavedInterests] = useState([]);
  const [customSections, setCustomSections] = useState({});
  const [savedWebsites, setSavedWebsites] = useState([]);
  
  const [leftSectionOrder, setLeftSectionOrder] = useState(defaultLeftSectionOrder);
  const [rightSectionOrder, setRightSectionOrder] = useState(defaultRightSectionOrder);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [dragOverSide, setDragOverSide] = useState(null);
  const [arrangeDialogOpen, setArrangeDialogOpen] = useState(false);

  // ── LocalStorage ────────────────────────────────────────────────────────
  useEffect(() => {
    try {
      const storedEntries = localStorage.getItem("educationEntries");
      if (storedEntries) setEducationEntries(JSON.parse(storedEntries));

      const storedSkills = localStorage.getItem("skills");
      if (storedSkills) setSavedSkills(JSON.parse(storedSkills));

      const storedSummaries = JSON.parse(localStorage.getItem("summaries") || "[]");
      if (storedSummaries.length > 0) setSavedSummary(storedSummaries[storedSummaries.length - 1]);

      setSavedAdditionalInfo(JSON.parse(localStorage.getItem("additionalInfo")      || "[]"));
      setSavedLanguages(     JSON.parse(localStorage.getItem("languagesList")       || "[]"));
      setSavedAccomplishments(JSON.parse(localStorage.getItem("accomplishmentsList") || "[]"));
      setSavedCertifications( JSON.parse(localStorage.getItem("certificationsList")  || "[]"));
      setSavedReferences(     JSON.parse(localStorage.getItem("referencesList")      || "[]"));
      setSoftwareSkills(      JSON.parse(localStorage.getItem("softwareSkills")      || "[]"));
      setSavedVolunteering(   JSON.parse(localStorage.getItem("volunteering")        || "[]"));
      setSavedInterests(      JSON.parse(localStorage.getItem("interests")           || "[]"));

      const userId = localStorage.getItem("userId");
      let sectionsData = {};

      if (userId) {
        const currentCustomSections = JSON.parse(localStorage.getItem(`current_custom_sections_${userId}`) || "[]");
        currentCustomSections.forEach(sectionName => {
          const userCustomKey = `custom_${userId}_${sectionName}`;
          try {
            const customSectionData = localStorage.getItem(userCustomKey);
            if (customSectionData) {
              const parsedData = JSON.parse(customSectionData);
              const items = parsedData.items || [];
              const validItems = items.filter(item => item && item.trim() !== '');
              if (validItems.length > 0) sectionsData[sectionName] = validItems;
            }
          } catch (error) {
            console.error(`Error loading custom section ${sectionName}:`, error);
          }
        });
      }
      setCustomSections(sectionsData);

      // Add this after loading other sections
// Load websites - handle both old and new format
const websitesData = localStorage.getItem("websites");
if (websitesData) {
  const parsed = JSON.parse(websitesData);
  // Ensure each website has the proper structure
  const normalized = parsed.map(item => ({
    url: item.url || item.name || item,
    label: item.label || "",
    addToHeader: item.addToHeader || false,
    name: item.name || item.url || item,
  }));
  setSavedWebsites(normalized);
}

      const savedLeftSectionOrder = localStorage.getItem("resume39LeftSectionOrder");
      const savedRightSectionOrder = localStorage.getItem("resume39RightSectionOrder");

      if (savedLeftSectionOrder) {
        const parsed = JSON.parse(savedLeftSectionOrder).filter(s => !s.startsWith("custom_"));
        setLeftSectionOrder(parsed);
      }
      
      const customIds = Object.keys(sectionsData).map(name => `custom_${name}`);
      if (savedRightSectionOrder) {
        const parsed = JSON.parse(savedRightSectionOrder);
        const filtered = parsed.filter(s => !s.startsWith("custom_"));
        setRightSectionOrder([...filtered, ...customIds]);
      } else {
        setRightSectionOrder([...defaultRightSectionOrder, ...customIds]);
      }
    } catch (e) {
      console.error("Resume39 load error:", e);
    }
  }, []);

  const formatDate = (month, year) => {
    if (!month || !year) return "";
    return `${month} ${year}`;
  };

  // ── Drag handlers ───────────────────────────────────────────────────────
  const mkDragStart = (side, order) => (e, idx) => {
    e.dataTransfer.setData("text/plain", JSON.stringify({ idx, side, id: order[idx] }));
    e.currentTarget.style.opacity = "0.5";
  };
  const handleDragOver  = (e, idx, side) => { e.preventDefault(); setDragOverIndex(idx); setDragOverSide(side); };
  const handleDragLeave = () => { setDragOverIndex(null); setDragOverSide(null); };
  const handleDragEnd   = e  => { e.currentTarget.style.opacity = "1"; setDragOverIndex(null); setDragOverSide(null); };

  const handleDrop = (e, targetIdx, targetSide) => {
    e.preventDefault();
    const { idx: srcIdx, side: srcSide, id } = JSON.parse(e.dataTransfer.getData("text/plain"));
    let lo = [...leftSectionOrder], ro = [...rightSectionOrder];
    if (srcSide === targetSide) {
      const arr = srcSide === "left" ? lo : ro;
      const [m] = arr.splice(srcIdx, 1);
      arr.splice(targetIdx, 0, m);
    } else {
      (srcSide === "left" ? lo : ro).splice(srcIdx, 1);
      (targetSide === "left" ? lo : ro).splice(targetIdx, 0, id);
    }
    setLeftSectionOrder(lo);  localStorage.setItem("resume39LeftSectionOrder",  JSON.stringify(lo));
    setRightSectionOrder(ro); localStorage.setItem("resume39RightSectionOrder", JSON.stringify(ro));
    setDragOverIndex(null); setDragOverSide(null);
    if (e.currentTarget) e.currentTarget.style.opacity = "1";
  };

  const resetAll = () => {
    setLeftSectionOrder(defaultLeftSectionOrder);
    setRightSectionOrder(defaultRightSectionOrder);
    localStorage.setItem("resume39LeftSectionOrder",  JSON.stringify(defaultLeftSectionOrder));
    localStorage.setItem("resume39RightSectionOrder", JSON.stringify(defaultRightSectionOrder));
  };

  const handleArrangeSectionsClick = () => setArrangeDialogOpen(true);
  const handleArrangeDialogClose = () => setArrangeDialogOpen(false);

  // ── Font & Heading scaling ───────────────────────────────────────────────
  const fontScale = (parseFloat(fs) || 14) / 14;
  const headingScale = (parseFloat(hs) || 24) / 24;

  const globalFontStyle = {
    fontFamily: font,
    fontSize: fs,
    fontStyle: fontStyle,
    lineHeight: lh,
    ...overflowText,
  };

  const getTextStyle = (additionalStyles = {}) => {
    const base = { ...globalFontStyle };
    const scaled = { ...additionalStyles };
    if (scaled.fontSize) {
      const sizeVal = parseFloat(scaled.fontSize);
      if (!isNaN(sizeVal)) {
        scaled.fontSize = `${sizeVal * fontScale}px`;
      }
    } else {
      scaled.fontSize = `${12 * fontScale}px`;
    }
    return {
      ...base,
      ...scaled
    };
  };

  // ── Custom Headers for Resume39 ──────────────────────────────────────────
  const SidebarHeader = ({ title }) => (
    <Box
      sx={{
        bgcolor: "#fff",
        color: color,
        px: 2,
        py: 0.6,
        borderRadius: "15px 0 15px 0",
        display: "inline-block",
        mb: `${Math.round(paraGap * 0.4)}px`,
        mt: `${Math.round(paraGap * 0.8)}px`,
        width: "fit-content",
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 900,
          fontSize: `${12 * headingScale}px`,
          letterSpacing: "1px",
          fontFamily: font,
          wordBreak: "break-word"
        }}
      >
        {title}
      </Typography>
    </Box>
  );

  const MainHeader = ({ title }) => (
    <Box
      sx={{
        bgcolor: color,
        color: "#fff",
        px: 2.5,
        py: 0.5,
        borderRadius: "20px",
        display: "inline-block",
        mb: `${Math.round(paraGap * 0.4)}px`,
        mt: `${Math.round(paraGap * 0.3)}px`,
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 700,
          fontSize: `${12 * headingScale}px`,
          letterSpacing: "0.5px",
          fontFamily: font,
          wordBreak: "break-word"
        }}
      >
        {title}
      </Typography>
    </Box>
  );

  // ── Left Sidebar render ─────────────────────────────────────────────────
  const renderLeftSection = (sectionId) => {
    switch (sectionId) {
      case "contact":
        return (data.phone || data.email || data.city || data.linkedIn) ? (
          <Box key="contact" style={{ ...overflowText }}>
            {photo && (
              <Box sx={{ mb: `${paraGap}px`, display: 'flex', justifyContent: 'center' }}>
                <Avatar src={photo} sx={{ width: 140, height: 140, border: `6px solid ${accentColor}` }} />
              </Box>
            )}
            <SidebarHeader title="CONTACT" />
            <Stack spacing={`${Math.max(paraGap / 8, 0.5)}px`}>
              {data.phone && <Typography sx={getTextStyle({ fontSize: "11px", color: "#fff" })}>📞 {data.phone}</Typography>}
              {data.email && <Typography sx={getTextStyle({ fontSize: "11px", color: "#fff" })}>📧 {data.email}</Typography>}
              {data.city && <Typography sx={getTextStyle({ fontSize: "11px", color: "#fff" })}>📍 {data.city}</Typography>}
              {data.linkedIn && (
                <Typography sx={getTextStyle({ fontSize: "11px", color: "#fff", display: 'flex', alignItems: 'center' })}>
                  <LinkedInIcon sx={{ fontSize: 14, mr: 0.5, flexShrink: 0 }} /> <span>{data.linkedIn}</span>
                </Typography>
              )}
            </Stack>
          </Box>
        ) : null;

      case "languages":
        return savedLanguages.length > 0 ? (
          <Box key="languages" style={{ ...overflowText }}>
            <SidebarHeader title="LANGUAGES" />
            <Stack spacing={`${Math.max(paraGap / 10, 1)}px`}>
              {savedLanguages.map((lang, idx) => (
                <Box key={idx}>
                  <Typography sx={getTextStyle({ fontSize: "11px", fontWeight: 500, color: "#fff" })}>
                    {lang.name} {lang.level && `(${lang.level})`}
                  </Typography>
                  <Box sx={{ width: "100%", height: "4px", bgcolor: "rgba(255,255,255,0.2)", mt: 0.5, borderRadius: 2 }}>
                    <Box sx={{ width: lang.level === 'Fluent' || lang.level === 'Native' ? "90%" : lang.level === 'Intermediate' ? "70%" : "50%", height: "100%", bgcolor: accentColor, borderRadius: 2 }} />
                  </Box>
                </Box>
              ))}
            </Stack>
          </Box>
        ) : null;

      case "skills":
        return (savedSkills.length > 0 || softwareSkills.length > 0) ? (
          <Box key="skills" style={{ ...overflowText }}>
            <SidebarHeader title="SKILLS" />
            <List dense sx={{ p: 0 }}>
              {savedSkills.map((skill, index) => (
                <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 20 }}><Box sx={{ width: 6, height: 6, bgcolor: accentColor }} /></ListItemIcon>
                  <ListItemText primary={<Typography sx={getTextStyle({ fontSize: "11px", color: "#fff" })}>{skill.name} {skill.rating && `(${skill.rating}%)`}</Typography>} />
                </ListItem>
              ))}
              {softwareSkills.map((skill, index) => (
                <ListItem key={`software-${index}`} sx={{ px: 0, py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 20 }}><Box sx={{ width: 6, height: 6, bgcolor: accentColor }} /></ListItemIcon>
                  <ListItemText primary={<Typography sx={getTextStyle({ fontSize: "11px", color: "#fff" })}>{skill.name} {skill.level && `(${skill.level}%)`}</Typography>} />
                </ListItem>
              ))}
            </List>
          </Box>
        ) : null;

      default:
        return renderRightSection(sectionId, true);
    }
  };

  // ── Right Main render ──────────────────────────────────────────────────
  const renderRightSection = (sectionId, inSidebar = false, filterIds = null) => {
    if (filterIds !== null && !filterIds.includes(sectionId)) return null;

    const tStyle = inSidebar ? getTextStyle({ color: "#fff" }) : getTextStyle({ color: "#555" });
    const dividerColor = inSidebar ? "rgba(255,255,255,0.3)" : color;

    const SectionBox = ({ children }) => (
      <Box
        className="resume-section"
        data-section-id={sectionId}
        style={{ marginBottom: `${secGap}px`, ...overflowText }}
      >
        {children}
      </Box>
    );

    const Header = ({ title }) => inSidebar ? <SidebarHeader title={title} /> : <MainHeader title={title} />;

    switch (sectionId) {
      case "profile":
        return (savedSummary || data.profile) ? (
          <SectionBox key="profile">
            <Header title="PERSONAL PROFILE" />
            <Typography sx={getTextStyle({ fontSize: "12px", color: inSidebar ? "#fff" : "#555" })}>{savedSummary || data.profile}</Typography>
          </SectionBox>
        ) : null;

      case "education":
        return educationEntries.length ? (
          <SectionBox key="education">
            <Header title="EDUCATION" />
            <Stack spacing={`${Math.max(paraGap / 10, 1)}px`}>
              {educationEntries.map((edu, index) => {
                const institutionName = edu.educationLevel === "school" ? edu.schoolName : edu.collegeName;
                const degreeName = edu.educationLevel === "school" ? edu.standard : edu.degreeName;
                const fieldOfStudy = edu.educationLevel === "school" ? edu.stream : edu.specialization;
                const percentage = edu.percentage;
                const cgpa = edu.cgpa;
                const grade = edu.grade;
                
                return (
                  <Box key={index} sx={{ mb: `${Math.round(paraGap * 0.5)}px` }}>
                    <Typography sx={getTextStyle({ fontWeight: 800, fontSize: "13px", color: inSidebar ? "#fff" : "#333" })}>
                      {degreeName} {fieldOfStudy && `- ${fieldOfStudy}`}
                    </Typography>
                    <Typography sx={getTextStyle({ fontSize: "12px", color: inSidebar ? accentColor : color, fontWeight: 600 })}>
                      {institutionName}
                    </Typography>
                    <Typography sx={getTextStyle({ fontSize: "11px", color: inSidebar ? "rgba(255,255,255,0.8)" : "#777" })}>
                      {percentage && `${percentage}%`}{cgpa && ` | CGPA: ${cgpa}`}{grade && ` | Grade: ${grade}`} | {formatDate(edu.gradMonth, edu.gradYear)}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          </SectionBox>
        ) : null;

      case "workExperience":
        return workExperiences && workExperiences.length ? (
          <SectionBox key="workExperience">
            <Header title="EXPERIENCE" />
            <Stack spacing={`${Math.max(paraGap / 10, 1)}px`}>
              {workExperiences.map((work, i) => (
                <Box key={i} sx={{ mb: `${Math.round(paraGap * 0.5)}px` }}>
                  <Typography sx={getTextStyle({ fontSize: "12px", fontWeight: 900, color: inSidebar ? accentColor : color })}>
                    ({formatDate(work.startMonth, work.startYear)} - {work.current ? "Present" : formatDate(work.endMonth, work.endYear)})
                  </Typography>
                  <Typography sx={getTextStyle({ fontWeight: 800, fontSize: "13px", color: inSidebar ? "#fff" : "#333" })}>
                    {work.jobTitle}
                  </Typography>
                  <Typography sx={getTextStyle({ fontSize: "12px", color: inSidebar ? "rgba(255,255,255,0.8)" : "#666" })}>{work.employer}</Typography>
                  {work.description && (
                    <Box sx={getTextStyle({ fontSize: "11px", color: inSidebar ? "rgba(255,255,255,0.8)" : "#777", mt: 1 })} dangerouslySetInnerHTML={{ __html: work.description }} />
                  )}
                </Box>
              ))}
            </Stack>
          </SectionBox>
        ) : null;

      case "certifications":
        return savedCertifications.length ? (
          <SectionBox key="certifications">
            <Header title="CERTIFICATIONS" />
            <Stack spacing={`${Math.max(paraGap / 10, 1)}px`}>
              {savedCertifications.map((cert, index) => (
                <Box key={index}>
                  <Typography sx={getTextStyle({ fontWeight: 800, fontSize: "13px", color: inSidebar ? "#fff" : "#333" })}>{cert.name}</Typography>
                  <Typography sx={getTextStyle({ fontSize: "12px", color: inSidebar ? "rgba(255,255,255,0.8)" : "#666" })}>{cert.provider} {cert.year && `(${cert.year})`}</Typography>
                </Box>
              ))}
            </Stack>
          </SectionBox>
        ) : null;

      case "accomplishments":
        return savedAccomplishments.length ? (
          <SectionBox key="accomplishments">
            <Header title="ACCOMPLISHMENTS" />
            <List dense sx={{ p: 0 }}>
              {savedAccomplishments.map((acc, index) => (
                <ListItem key={index} sx={{ px: 0, py: 0.3 }}>
                  <ListItemIcon sx={{ minWidth: 20 }}><Box sx={{ width: 6, height: 6, bgcolor: inSidebar ? accentColor : color }} /></ListItemIcon>
                  <ListItemText primary={<Typography sx={getTextStyle({ fontSize: "12px", color: inSidebar ? "#fff" : "#333" })}>{acc}</Typography>} />
                </ListItem>
              ))}
            </List>
          </SectionBox>
        ) : null;

      case "volunteering":
        return savedVolunteering.length ? (
          <SectionBox key="volunteering">
            <Header title="VOLUNTEERING" />
            <Stack spacing={`${Math.max(paraGap / 10, 1)}px`}>
              {savedVolunteering.map((vol, index) => (
                <Box key={index} sx={{ mb: `${Math.round(paraGap * 0.5)}px` }}>
                  <Typography sx={getTextStyle({ fontWeight: 800, fontSize: "13px", color: inSidebar ? "#fff" : "#333" })}>{vol.subtopic}</Typography>
                  <Typography sx={getTextStyle({ fontSize: "12px", color: inSidebar ? "rgba(255,255,255,0.8)" : "#666" })}>{vol.fromDate} - {vol.toDate}</Typography>
                  <Typography sx={getTextStyle({ fontSize: "11px", color: inSidebar ? "rgba(255,255,255,0.8)" : "#777" })}>{vol.content}</Typography>
                </Box>
              ))}
            </Stack>
          </SectionBox>
        ) : null;

      case "interests":
        return savedInterests.length ? (
          <SectionBox key="interests">
            <Header title="INTERESTS" />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: `4px ${Math.round(paraGap * 0.5)}px` }}>
              {savedInterests.map((interest, index) => (
                <Typography key={index} sx={getTextStyle({ fontSize: "12px", bgcolor: inSidebar ? "rgba(255,255,255,0.15)" : `${color}20`, color: inSidebar ? "#fff" : color, px: 2, py: 0.5, borderRadius: "15px" })}>{interest}</Typography>
              ))}
            </Box>
          </SectionBox>
        ) : null;

      case "websites":
  return savedWebsites.length ? (
    <SectionBox key="websites">
      <Header title="WEBSITES & PROFILES" />
      <Stack spacing={`${Math.max(paraGap / 10, 1)}px`}>
        {savedWebsites.map((site, i) => (
          <Box key={i} style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <LinkIcon style={{ fontSize: "14px", color: inSidebar ? accentColor : color }} />
            <Typography sx={getTextStyle({ 
              fontSize: "12px", 
              color: inSidebar ? "rgba(255,255,255,0.8)" : "#555" 
            })}>
              {site.label ? (
                <>
                  <span style={{ fontWeight: "bold" }}>{site.label}:</span> {site.url}
                </>
              ) : (
                site.url
              )}
              {site.addToHeader && (
                <span style={{ 
                  fontSize: "8px", 
                  color: inSidebar ? accentColor : color, 
                  fontWeight: "bold",
                  marginLeft: "4px",
                  backgroundColor: inSidebar ? "rgba(255,255,255,0.2)" : `${color}20`,
                  padding: "1px 6px",
                  borderRadius: "3px"
                }}>
                  Header
                </span>
              )}
            </Typography>
          </Box>
        ))}
      </Stack>
    </SectionBox>
  ) : null;

      case "additionalInfo":
        return savedAdditionalInfo.length ? (
          <SectionBox key="additionalInfo">
            <Header title="ADDITIONAL INFO" />
            <List dense sx={{ p: 0 }}>
              {savedAdditionalInfo.map((info, index) => (
                <ListItem key={index} sx={{ px: 0, py: 0.3 }}>
                  <ListItemIcon sx={{ minWidth: 20 }}><Box sx={{ width: 6, height: 6, bgcolor: inSidebar ? accentColor : color }} /></ListItemIcon>
                  <ListItemText primary={<Typography sx={getTextStyle({ fontSize: "12px", color: inSidebar ? "#fff" : "#333" })}>{info}</Typography>} />
                </ListItem>
              ))}
            </List>
          </SectionBox>
        ) : null;

      case "references":
        return savedReferences.length ? (
          <SectionBox key="references">
            <Header title="REFERENCES" />
            <Stack spacing={`${Math.max(paraGap / 10, 1)}px`}>
              {savedReferences.map((ref, index) => (
                <Typography key={index} sx={getTextStyle({ fontSize: "12px", color: inSidebar ? "rgba(255,255,255,0.8)" : "#777" })}>{ref}</Typography>
              ))}
            </Stack>
          </SectionBox>
        ) : null;

      default:
        if (sectionId.startsWith("custom_")) {
          const name  = sectionId.replace("custom_", "");
          const items = customSections[name] || [];
          return items.length ? (
            <SectionBox key={sectionId}>
              <Header title={name.replace(/_/g, ' ')} />
              <List dense sx={{ p: 0 }}>
                {items.map((item, i) => (
                  <ListItem key={i} sx={{ px: 0, py: 0.3 }}>
                    <ListItemIcon sx={{ minWidth: 20 }}><Box sx={{ width: 6, height: 6, bgcolor: inSidebar ? accentColor : color }} /></ListItemIcon>
                    <ListItemText primary={<Typography sx={getTextStyle({ fontSize: "12px", color: inSidebar ? "#fff" : "#333" })}>{item}</Typography>} />
                  </ListItem>
                ))}
              </List>
            </SectionBox>
          ) : null;
        }
        return null;
    }
  };

  // ── Pagination logic ─────────────────────────────────────────────────────
  const availableHeight = page.height - (2 * tbPad);

  const paginateContent = useCallback(() => {
    if (!mainContentRef.current) return;
    const sections = Array.from(mainContentRef.current.querySelectorAll(".resume-section"));
    if (sections.length === 0) { setPages([{ rightIds: [] }]); return; }

    const newPages = [];
    let currentIds = [];
    let currentHeight = 0;

    sections.forEach(section => {
      const id = section.getAttribute("data-section-id");
      const h  = section.offsetHeight;
      const targetAvailableHeight = newPages.length === 0 ? (availableHeight - 160) : availableHeight;

      if (currentHeight + h > targetAvailableHeight && currentIds.length > 0) {
        newPages.push({ rightIds: currentIds });
        currentIds = [id];
        currentHeight = h;
      } else {
        currentIds.push(id);
        currentHeight += h;
      }
    });
    if (currentIds.length > 0) newPages.push({ rightIds: currentIds });
    setPages(newPages);
  }, [
    availableHeight, rightSectionOrder,
    savedSummary, workExperiences, savedLanguages, savedAccomplishments,
    savedCertifications, softwareSkills, savedVolunteering, savedInterests,
    savedReferences, savedAdditionalInfo, customSections,
    fontSize, headingSize, font, fontStyle, lineSpacing, paragraphSpacing, sectionSpacing,
  ]);

  useEffect(() => {
    const id = setTimeout(() => paginateContent(), 400);
    return () => clearTimeout(id);
  }, [paginateContent]);

  const getSectionTitle = id => {
    const map = {
      contact: "Contact", education: "Education", skills: "Skills",
      profile: "Profile", workExperience: "Work Experience", languages: "Languages",
      accomplishments: "Accomplishments", certifications: "Certifications",
      softwareSkills: "Software Skills", volunteering: "Volunteering",
      interests: "Interests", websites: "Websites/Profiles",
      references: "References", additionalInfo: "Additional Info",
    };
    if (id.startsWith("custom_")) return id.replace("custom_", "").replace(/_/g, " ").toUpperCase();
    return map[id] || id;
  };

  // ── Layout Style properties ──────────────────────────────────────────────
  const pageStyle = {
    width:          `${page.width}px`,
    minHeight:      `${page.height}px`,
    maxWidth:       `${page.width}px`,
    overflow:       "hidden",
    display:        "flex",
    flexDirection:  "row",
    backgroundColor: "#ffffff",
    boxShadow:      exporting ? "none" : "0 4px 20px rgba(0,0,0,0.15)",
    margin:         "0 auto 24px",
    boxSizing:      "border-box",
    fontFamily:     font,
    fontSize:       fs,
  };

  const sidebarStyle = {
    width:           `${SIDEBAR_W}px`,
    minWidth:        `${SIDEBAR_W}px`,
    maxWidth:        `${SIDEBAR_W}px`,
    minHeight:       `${page.height}px`,
    overflow:        "hidden",
    backgroundColor: color,
    color:           "white",
    paddingTop:      `${tbPad}px`,
    paddingBottom:   `${tbPad}px`,
    paddingLeft:     `${Math.max(10, Math.round(sidePad * 0.6))}px`,
    paddingRight:    `${Math.max(10, Math.round(sidePad * 0.6))}px`,
    boxSizing:       "border-box",
    display:         "flex",
    flexDirection:   "column",
    gap:             `${Math.max(8, Math.round(secGap * 0.6))}px`,
    flexShrink:      0,
  };

  const mainAreaStyle = {
    width:         `${MAIN_W}px`,
    minWidth:      0,
    minHeight:     `${page.height}px`,
    paddingTop:    `${tbPad}px`,
    paddingBottom: `${tbPad}px`,
    paddingLeft:   `${sidePad}px`,
    paddingRight:  `${sidePad}px`,
    boxSizing:     "border-box",
    display:       "flex",
    flexDirection: "column",
    overflow:      "hidden",
  };

  // ── Drag panel rendering inside Dialog ──────────────────────────────────
  const renderDragPanel = (side, sections) => (
    <Box style={{ flex: 1 }}>
      <Typography variant="h6" style={{ marginBottom: "12px", fontWeight: 600 }}>
        {side === "left" ? "Left (Sidebar)" : "Right (Main)"} — {sections.length} sections
      </Typography>
      <Box style={{ maxHeight: 400, overflowY: "auto", border: "1px solid #e0e0e0", borderRadius: 4, padding: 8 }}>
        {sections.map((id, idx) => (
          <Paper key={id} draggable
            onDragStart={e => mkDragStart(side, sections)(e, idx)}
            onDragOver={e => handleDragOver(e, idx, side)}
            onDragLeave={handleDragLeave}
            onDrop={e => handleDrop(e, idx, side)}
            onDragEnd={handleDragEnd}
            style={{
              padding: "10px 14px",
              marginBottom: 8,
              display: "flex",
              alignItems: "center",
              backgroundColor: dragOverIndex === idx && dragOverSide === side ? "#f0f0f0" : "white",
              border: dragOverIndex === idx && dragOverSide === side
                ? `2px dashed ${color}`
                : "1px solid #ddd",
              cursor: "grab",
            }}>
            <DragIndicator color="action" style={{ marginRight: 8 }} />
            <Typography style={{ flex: 1, fontSize: "13px" }}>{getSectionTitle(id)}</Typography>
            <Typography variant="body2" color="textSecondary">{idx + 1}</Typography>
          </Paper>
        ))}
        {!sections.length && (
          <Typography style={{ padding: 16, textAlign: "center", color: "#888", fontSize: "13px" }}>
            Drag sections here from the other side.
          </Typography>
        )}
      </Box>
      <Button size="small" variant="outlined" style={{ marginTop: 8 }}
        onClick={() => side === "left"
          ? (setLeftSectionOrder(defaultLeftSectionOrder),  localStorage.setItem("resume39LeftSectionOrder",  JSON.stringify(defaultLeftSectionOrder)))
          : (setRightSectionOrder(defaultRightSectionOrder), localStorage.setItem("resume39RightSectionOrder", JSON.stringify(defaultRightSectionOrder)))}>
        Reset {side === "left" ? "Left" : "Right"}
      </Button>
    </Box>
  );

  const totalPages = Math.max(1, pages.length);

  return (
    <Box sx={{ width: "100%", position: "relative", m: 0 }}>
      {/* Controls */}
      {!exporting && (
        <Box style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 16, flexWrap: "wrap", justifyContent: "center" }}>
          <IconButton onClick={() => setCurrentPage(p => Math.max(0, p - 1))} disabled={currentPage === 0} size="small">
            <ChevronLeft />
          </IconButton>
          <Typography variant="body2">Page {currentPage + 1} of {totalPages}</Typography>
          <IconButton size="small" onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))} disabled={currentPage >= totalPages - 1}>
            <ChevronRight />
          </IconButton>
          {enableDragDrop && (
            <>
              <Button variant="contained" size="small" startIcon={<Reorder />} onClick={handleArrangeSectionsClick}>
                Arrange Sections
              </Button>
              <Button variant="outlined" size="small" color="secondary" onClick={resetAll}>
                Reset All
              </Button>
            </>
          )}
        </Box>
      )}

      {/* Hidden measurement container — renders ALL right sections to measure heights */}
      <Box
        ref={mainContentRef}
        style={{
          position: "absolute", top: -9999, visibility: "hidden", pointerEvents: "none",
          width: `${MAIN_W}px`,
          paddingLeft: `${sidePad}px`, paddingRight: `${sidePad}px`,
          fontFamily: font, fontSize: fs,
          boxSizing: "border-box",
        }}
      >
        {rightSectionOrder.map(id => (
          <React.Fragment key={id}>{renderRightSection(id, false)}</React.Fragment>
        ))}
      </Box>

      {/* Resume Pages */}
      {(pages.length > 0 ? pages : [{ rightIds: null }]).map((pageContent, pageIdx) => (
        <Box
          key={pageIdx}
          className="resume-page"
          style={{
            ...pageStyle,
            display: exporting ? "flex" : (pageIdx === currentPage ? "flex" : "none"),
          }}
        >
          {/* SIDEBAR — always shown on every page */}
          <Box style={sidebarStyle}>
            {pageIdx === 0 && leftSectionOrder.map((id, i) => {
              const content = renderLeftSection(id);
              if (!content) return null;
              return (
                <React.Fragment key={id}>
                  {i > 0 && (
                    <Divider style={{ borderColor: "rgba(255,255,255,0.25)", borderBottomWidth: lineWeight }} />
                  )}
                  {content}
                </React.Fragment>
              );
            })}
          </Box>

          {/* MAIN CONTENT — only sections assigned to this page */}
          <Box style={mainAreaStyle}>
            {pageIdx === 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h3" sx={{ fontWeight: 900, color: color, lineHeight: 0.8, fontSize: "42px", fontFamily: font, wordBreak: 'break-word' }}>
                  {(data.firstName || "").toUpperCase()}
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 400, color: color, fontSize: "42px", mb: 1, fontFamily: font, wordBreak: 'break-word' }}>
                  {(data.lastName || "").toUpperCase()}
                </Typography>
                {data.currentPosition && (
                  <Typography variant="h6" sx={{ color: "#444", fontWeight: 500, letterSpacing: 5, fontSize: "16px", fontFamily: font, wordBreak: 'break-word', mt: 1 }}>
                    {data.currentPosition.toUpperCase()}
                  </Typography>
                )}
              </Box>
            )}
            
            <Box style={{ flex: 1, overflow: "hidden", minWidth: 0 }}>
              {rightSectionOrder.map(id => (
                <React.Fragment key={id}>
                  {renderRightSection(id, false, pageContent.rightIds)}
                </React.Fragment>
              ))}
            </Box>
          </Box>
        </Box>
      ))}

      {/* Arrange Dialog */}
      <Dialog open={arrangeDialogOpen} onClose={handleArrangeDialogClose} maxWidth="md" fullWidth>
        <DialogTitle style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Reorder /> Arrange Resume Sections
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
            Drag to reorder sections or move them between sidebar and main content area.
          </Typography>
          <Box style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
            {renderDragPanel("left",  leftSectionOrder)}
            {renderDragPanel("right", rightSectionOrder)}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetAll} color="secondary">Reset All</Button>
          <Button variant="contained" onClick={handleArrangeDialogClose}>Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Resume39;